import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../i18n/content';

const STEP_IMAGES = [
  '/assets/images/laptop.png',
  'https://images.unsplash.com/photo-1510557880182-3d4d3cba35a5?w=800&h=600&fit=crop&q=80',
  '/assets/images/developer.png',
  '/assets/images/dashboard.png'
];

const Trust = () => {
  const { language } = useLanguage();
  const t = content[language].trust;
  const fadeInUp = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8 }
  };

  const steps = t.steps.map((step, i) => ({ ...step, image: STEP_IMAGES[i] }));

  return (
    <section id="trust" className="py-24 px-4 relative premium-bg-3">
      <div className="container mx-auto max-w-6xl relative z-10 premium-content-layer">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-24"
          {...fadeInUp}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="text-gold text-sm">◆</span>
            <span className="text-white/70 text-sm font-medium tracking-widest uppercase">{t.badge}</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
            {t.titleLine1} <span className="text-secondary block mt-2">{t.titleLine2}</span>
          </h2>
          <p className="text-xl text-white/70 font-light leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="space-y-32">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className={`flex flex-col md:flex-row items-center gap-12 lg:gap-24 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <div className="md:w-1/2 relative z-10">
                <div className="text-[10rem] md:text-[14rem] font-display font-bold text-white/5 absolute -top-20 md:-top-32 -left-10 md:-left-16 -z-10 leading-none select-none">
                  0{index + 1}
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-1 tracking-wide">{step.title}</h3>
                <div className="w-10 h-0.5 bg-secondary rounded-full mb-6"></div>
                <p className="text-lg md:text-xl text-light-gray/80 font-light leading-relaxed">{step.desc}</p>
              </div>
              <div className="md:w-1/2 w-full flex justify-center">
                <div className="w-full h-64 md:h-80 rounded-[2.5rem] border border-white/10 relative overflow-hidden group">
                  {/* Image */}
                  <img
                    src={step.image}
                    alt={step.title}
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                  {/* Light cinematic overlay */}
                  <div className="absolute inset-0 bg-primary/30"></div>
                  {/* Subtle blue radial glow */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,123,255,0.1)_0%,_transparent_70%)]"></div>
                  {/* Soft bottom fade */}
                  <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-primary/60 to-transparent"></div>
                  {/* Hover blue overlay */}
                  <div className="absolute inset-0 bg-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  {/* Step badge */}
                  <div className="absolute top-6 left-6 w-10 h-10 rounded-full bg-secondary/20 border border-secondary/30 flex items-center justify-center backdrop-blur-sm">
                    <span className="text-secondary text-xs font-bold">0{index + 1}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Trust;
